import { apiClient } from './client';
import type { DashboardInitPayload } from '../types/dashboard.types';

/**
 * Fetches the initial dashboard data from the BFF.
 * Aggregates user, org, quota, agents, sessions, and files.
 */
export const fetchDashboardInit = async (): Promise<DashboardInitPayload> => {
  const { data } = await apiClient.get('/api/v1/dashboard/init');
  return data.data; // BFF wraps success/data results
};

/**
 * Downloads a single file from the vault via BFF.
 */
export const downloadFile = async (fileId: string, filename: string): Promise<void> => {
  const response = await apiClient.get(`/api/v1/dashboard/files/${encodeURIComponent(fileId)}/download`, {
    responseType: 'blob',
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  try {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
};

/**
 * Deletes a single file from the vault via BFF.
 */
export const deleteFile = async (fileId: string): Promise<void> => {
  await apiClient.delete(`/api/v1/dashboard/files/${encodeURIComponent(fileId)}`);
};

/**
 * Downloads multiple files as a ZIP archive via BFF.
 */
export const bulkDownloadFiles = async (fileIds: string[]): Promise<void> => {
  const response = await apiClient.post('/api/v1/dashboard/files/bulk-download', { fileIds }, {
    responseType: 'blob',
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  try {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'reports_export.zip');
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
};

/**
 * Deletes multiple files from the vault via BFF.
 */
export const bulkDeleteFiles = async (fileIds: string[]): Promise<void> => {
  await apiClient.post('/api/v1/dashboard/files/bulk-delete', { fileIds });
};
